import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api, formatMoney, mediaUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Trash2, Edit, Code2, Eye, Heart, MessageCircle, Tag, Clock, Zap, Package } from 'lucide-react';
import { toast } from 'sonner';
import { AdEmbedModal } from '@/components/marketplace/AdEmbedModal';

const STATUS_LABELS = {
    published: { label: 'Activo',    color: 'bg-emerald-100 text-emerald-800' },
    draft:     { label: 'Borrador',  color: 'bg-slate-100 text-slate-700' },
    sold:      { label: 'Vendido',   color: 'bg-blue-100 text-blue-800' },
    expired:   { label: 'Expirado',  color: 'bg-red-100 text-red-800' },
    paused:    { label: 'Pausado',   color: 'bg-amber-100 text-amber-800' },
};
const TYPE_LABELS = {
    gratuita: { label: 'Gratuita', color: 'bg-slate-100 text-slate-600' },
    prime:    { label: '★ Prime',  color: 'bg-amber-100 text-amber-700' },
};
const FILTERS = [
    { key: 'all',       label: 'Todos' },
    { key: 'published', label: 'Activos' },
    { key: 'paused',    label: 'Pausados' },
    { key: 'sold',      label: 'Vendidos' },
    { key: 'expired',   label: 'Expirados' },
    { key: 'draft',     label: 'Borradores' },
];

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'hace un momento';
    if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
    if (diff < 2592000) return `hace ${Math.floor(diff / 86400)} días`;
    return `hace ${Math.floor(diff / 2592000)} mes(es)`;
}

function timeRemaining(dateStr, days = 90) {
    if (!dateStr) return null;
    const rem = new Date(dateStr).getTime() + days * 86400000 - Date.now();
    if (rem <= 0) return { label: 'Expirado', urgent: true };
    const d = Math.floor(rem / 86400000);
    if (d >= 1) return { label: `${d}d restantes`, urgent: d <= 7 };
    return { label: `${Math.floor(rem / 3600000)}h restantes`, urgent: true };
}

export default function MyAdsPage() {
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [embedAd, setEmbedAd] = useState(null);
    const [filter, setFilter] = useState('all');

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get('/me/ads'); setAds(data); }
        catch { toast.error('Error al cargar anuncios'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const del = async (id) => {
        if (!window.confirm('¿Eliminar este anuncio? Esta acción no se puede deshacer.')) return;
        try { await api.delete(`/ads/${id}`); toast.success('Anuncio eliminado'); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const filtered = useMemo(() => filter === 'all' ? ads : ads.filter(a => a.status === filter), [ads, filter]);
    const counts = useMemo(() => {
        const c = { all: ads.length };
        FILTERS.forEach(f => { if (f.key !== 'all') c[f.key] = ads.filter(a => a.status === f.key).length; });
        return c;
    }, [ads]);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold tracking-tight">Mis anuncios</h1>
                <Button asChild size="sm" className="bg-red-600 hover:bg-red-700">
                    <Link to="/dashboard/ads/new" data-testid="my-ads-new">+ Nuevo anuncio</Link>
                </Button>
            </div>

            {/* Filter tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                {FILTERS.map(f => (
                    <button key={f.key} onClick={() => setFilter(f.key)}
                        className={`shrink-0 text-xs px-3 py-1.5 rounded-full border font-medium transition-colors whitespace-nowrap ${
                            filter === f.key
                                ? 'bg-red-600 text-white border-red-600'
                                : 'bg-white text-muted-foreground border-border hover:border-red-300 hover:text-red-600'
                        }`}>
                        {f.label}{counts[f.key] > 0 ? ` (${counts[f.key]})` : ''}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Cargando anuncios…</div>
            ) : filtered.length === 0 ? (
                <div className="rounded-2xl border bg-card p-10 text-center">
                    <Package className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-sm text-muted-foreground">
                        {filter === 'all' ? 'Aún no tienes anuncios.' : `Sin anuncios en "${FILTERS.find(f => f.key === filter)?.label}".`}
                    </p>
                    {filter === 'all' && (
                        <Button asChild className="mt-4 bg-red-600 hover:bg-red-700">
                            <Link to="/dashboard/ads/new">Publicar el primero</Link>
                        </Button>
                    )}
                </div>
            ) : (
                <div className="space-y-3" data-testid="my-ads-list">
                    {filtered.map(a => {
                        const statusInfo = STATUS_LABELS[a.status] || { label: a.status, color: 'bg-slate-100 text-slate-700' };
                        const typeInfo = TYPE_LABELS[a.ad_type] || TYPE_LABELS.gratuita;
                        const rem = (a.ad_type === 'gratuita' && a.status === 'published') ? timeRemaining(a.created_at) : null;

                        return (
                            <div key={a.id} className="rounded-2xl border bg-card p-3 flex gap-3 items-start hover:shadow-sm transition-shadow" data-testid={`my-ad-${a.id}`}>
                                {/* Thumbnail */}
                                <Link to={`/ad/${a.id}`} className="shrink-0 hover:opacity-90 transition-opacity">
                                    <div className="h-24 w-24 rounded-xl overflow-hidden bg-neutral-100">
                                        {a.media?.[0]?.url ? (
                                            a.media[0].kind === 'youtube'
                                                ? <img src={`https://img.youtube.com/vi/${a.media[0].url}/hqdefault.jpg`} alt="" className="h-full w-full object-cover" />
                                                : a.media[0].kind === 'video'
                                                    ? <video src={mediaUrl(a.media[0].url)} muted className="h-full w-full object-cover" />
                                                    : <img src={mediaUrl(a.media[0].url)} alt="" className="h-full w-full object-cover" />
                                        ) : <div className="h-full w-full flex items-center justify-center"><Package className="h-7 w-7 text-muted-foreground/30" /></div>}
                                    </div>
                                </Link>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    {/* Badges row */}
                                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusInfo.color}`}>{statusInfo.label}</span>
                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${typeInfo.color}`}>{typeInfo.label}</span>
                                        <span className="text-[10px] text-muted-foreground capitalize">{(a.condition || '').replace('_', ' ')}</span>
                                    </div>

                                    {/* Title */}
                                    <Link to={`/ad/${a.id}`} className="font-medium text-sm line-clamp-2 hover:text-red-600 transition-colors leading-tight">
                                        {a.title}
                                    </Link>

                                    {/* Price */}
                                    <div className="text-base font-bold text-red-600 mt-0.5">{formatMoney(a.price, a.currency)}</div>

                                    {/* Stats row */}
                                    <div className="mt-1.5 flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                                        <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{a.views || 0} vistas</span>
                                        <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{a.favorites_count || 0}</span>
                                        {a.questions_count > 0 && <span className="flex items-center gap-1 text-blue-600 font-medium"><MessageCircle className="h-3 w-3" />{a.questions_count} preg.</span>}
                                        {a.comments_count > 0 && <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" />{a.comments_count} com.</span>}
                                        {a.offers_count > 0 && <span className="flex items-center gap-1 text-emerald-600 font-medium"><Tag className="h-3 w-3" />{a.offers_count} ofertas</span>}
                                    </div>

                                    {/* Time row */}
                                    <div className="mt-1 flex items-center gap-3 text-[10px] text-muted-foreground flex-wrap">
                                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{timeAgo(a.created_at)}</span>
                                        {rem && (
                                            <span className={`flex items-center gap-1 font-semibold ${rem.urgent ? 'text-red-500' : 'text-emerald-600'}`}>
                                                <Zap className="h-3 w-3" />{rem.label}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex flex-col gap-1.5 shrink-0">
                                    <Button size="sm" variant="outline" asChild className="h-8 w-8 p-0" title="Editar">
                                        <Link to={`/dashboard/ads/${a.id}/edit`}><Edit className="h-3.5 w-3.5" /></Link>
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-8 w-8 p-0" title="Embed" onClick={() => setEmbedAd(a)} data-testid={`ad-embed-${a.id}`}>
                                        <Code2 className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-8 w-8 p-0 hover:border-red-300 hover:text-red-600" title="Eliminar" onClick={() => del(a.id)} data-testid={`ad-delete-${a.id}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <AdEmbedModal open={!!embedAd} onOpenChange={() => setEmbedAd(null)} ad={embedAd} />
        </div>
    );
}
